import * as React from "react";
import { Client } from "@src/lib/services/server/contentful";
import { Navigation, Pagination, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { ProductContainer, SmartLink, ContentfulImage } from "@src/components";
import { ContentfulImageUrlTransforms } from "@src/lib/utils";
import Arrow from "@/img/icons/arrow.svg";
import External from "@/img/icons/external.svg";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

export type ProductClientsCarouselBlockProps = {
  id?: string | null;
  heading?: string | null;
  items: Client[];
};

const returnLineBreaks = (text: string) => {
  if (text.includes("\n")) {
    return text.split("\n").map((innerText: string, i: number) => {
      return (
        <span key={i} className="block">
          {innerText}
        </span>
      );
    });
  } else {
    return text;
  }
};

export function ProductClientsCarouselBlock({
  id = "",
  heading,
  items
}: ProductClientsCarouselBlockProps) {
  const dimensions = {
    width: 1024,
    height: 696
  };

  const transforms: ContentfulImageUrlTransforms = {
    fit: "fill" as const,
    focus: "center" as const,
    format: "webp" as const,
    quality: 50,
    width: dimensions.width,
    height: dimensions.height
  };

  const paginationClassName: string = `pagination-${id as string}`;
  const prevClassName: string = `prev-${id as string}`;
  const nextClassName: string = `next-${id as string}`;

  const renderItems = (items: Client[]) => {
    return items.map((item, index) => {
      const image = item.leadImage;
      const ctaUrl = item.ctaUrl as string;
      if (!ctaUrl) {
        return (
          <SwiperSlide
            key={index}
            className="flex !h-auto w-[258px!important] md:w-[480px!important] lg:w-[924px!important]"
          >
            <div className="group flex w-full">
              <figure className="w-full space-y-3 lg:space-y-6">
                <ContentfulImage
                  image={image}
                  transforms={transforms}
                  className="rounded-md md:rounded-3xl"
                  priority={true}
                />
                <figcaption className="!inline-flex items-center gap-2 text-pr-red-600">
                  <span className="type-pr-epsilon xl:text-pr-7xl">
                    {item.title}
                  </span>
                </figcaption>
              </figure>
            </div>
          </SwiperSlide>
        );
      }
      return (
        <SwiperSlide
          key={index}
          className="flex !h-auto w-[258px!important] md:w-[480px!important] lg:w-[924px!important]"
        >
          <SmartLink href={ctaUrl} className="group flex w-full">
            <figure className="w-full space-y-3 lg:space-y-6">
              <ContentfulImage
                image={image}
                transforms={transforms}
                className="rounded-md md:rounded-3xl"
                priority={true}
              />
              <figcaption className="link-underline-animated-sm xl:link-underline-animated-lg !inline-flex items-center gap-2 text-pr-red-600">
                <span className="type-pr-epsilon xl:text-pr-7xl">
                  {item.title}
                </span>
                <External className="size-4 fill-current xl:size-5" />
              </figcaption>
            </figure>
          </SmartLink>
        </SwiperSlide>
      );
    });
  };

  return (
    <div
      className="my-10 md:my-16 lg:my-24"
      id={heading?.toLowerCase().split(" ").join("-")}
    >
      <ProductContainer>
        <div className="space-y-8 lg:space-y-16">
          <div className="mx-auto text-center">
            {heading && (
              <h2 className="type-pr-beta">{returnLineBreaks(heading)}</h2>
            )}
          </div>
          <div className="relative after:absolute after:right-0 after:top-0 after:z-[1] after:h-full after:w-6 after:bg-gradient-to-l after:from-white after:to-transparent after:content-[''] md:after:w-20">
            <Swiper
              modules={[Navigation, Pagination, A11y]}
              spaceBetween={16}
              slidesPerView={"auto"}
              navigation={{
                prevEl: `.${prevClassName}`,
                nextEl: `.${nextClassName}`
              }}
              pagination={{
                clickable: true,
                bulletActiveClass: "swiper-pagination-bullet-active-product",
                bulletClass: "swiper-pagination-bullet-product",
                el: `.${paginationClassName}`
              }}
              breakpoints={{
                768: {
                  spaceBetween: 28
                },
                1024: {
                  spaceBetween: 50
                }
              }}
            >
              {renderItems(items)}
            </Swiper>

            <div
              className={`mt-8 flex flex-wrap items-center justify-center gap-2.5 px-24 py-4 lg:mt-12 lg:px-32 lg:py-5 ${paginationClassName}`}
            ></div>
            <div className="absolute bottom-0 right-0 z-[2] flex gap-px">
              <button
                className={`flex items-center justify-center rounded-l-full bg-pr-red-500 px-2.5 py-3.5 text-white transition disabled:bg-pr-red-100 lg:px-4 lg:py-5 ${prevClassName}`}
              >
                <Arrow className="h-3 w-5 rotate-180 fill-current" />
              </button>
              <button
                className={`flex items-center justify-center rounded-r-full bg-pr-red-500 px-2.5 py-3.5 text-white transition disabled:bg-pr-red-100 lg:px-4 lg:py-5 ${nextClassName}`}
              >
                <Arrow className="h-3 w-5 fill-current" />
              </button>
            </div>
          </div>
        </div>
      </ProductContainer>
    </div>
  );
}

export default ProductClientsCarouselBlock;
